// The order page's Phase 6 parts (plan omni-rick-round 6C, mock M5 orders-and-tickets.html):
// the tickets an order opened in Autotask, drawn for Phoneware with their queue, number, checklist
// and a link into Autotask; "Phoneware is setting it up" for a dealer; the ticket step's
// sentence; and the due date and numbers set until the order is complete by whoever holds orders.edit,
// with each line's Telephone Number for billing approval (#1763), saved with PATCH /api/orders/:id.
import React, { useState } from "react";
import { sellerLineNumber, telephoneDigits, TELEPHONE_NUMBER_FEATURE, type BillingRequirements, type Order, type OrderPatch } from "@phoneware/lens-domain";
import { ApiError } from "../api.js";
import { Icon } from "../icons.js";
import { useLoad, useSession } from "../session.js";
import { errorText, phoneWords, plural } from "../format.js";
import {
  SIDE_NOTE,
  dueText,
  followsPartner,
  numberCount,
  ticketLabel,
  ticketRows,
  type OrderSide,
  type TicketRow,
} from "../order-tickets.js";
import { atTime } from "../format.js";

// ------------------------------------------------------------------ the ticket step's sentence

/**
 * The "Tickets in Autotask" step as a sentence, or null for an order from before this round (its old sentence stands).
 * Phoneware reads queues and errors; a dealer reads what Phoneware is doing about it.
 */
export function ticketNote(o: Order, side: OrderSide | null, ops: boolean): string | null {
  const s = o.autotask;
  if (o.kind === "mac") {
    const n = s.ticketNumber ?? o.tickets?.find((t) => t.kind === "source")?.ticketNumber ?? "";
    if (s.state === "done") return `Omni added the MAC summary PDF and a note to ticket ${n}, where the MAC came from. No new ticket and no charges.`;
    if (s.state === "failed") return ops ? `Failed: ${s.error ?? "Autotask did not answer."}` : "Omni is adding a note to the ticket the MAC came from.";
    if (s.state === "running") return `Adding the MAC summary PDF and a note to ticket ${n}.`;
    return null;
  }
  if (!followsPartner(o)) return null;
  const rows = ticketRows(o, side);
  const ord = rows.find((r) => r.kind === "order" && r.ticketNumber);
  const prov = rows.find((r) => r.kind === "provisioning");
  const charges = plural(s.chargeIds.length, "charge");
  if (s.state === "failed") {
    const missing = rows.find((r) => !r.ticketNumber && r.state === "failed");
    if (!ops) return ord ? `Ticket ${ord.ticketNumber} is in.${side === "reseller" ? "" : " Phoneware\u2019s setup ticket is on its way."}` : "Omni is opening the ticket in Autotask.";
    const why = s.error ?? "Autotask did not answer.";
    if (!ord) return `Failed: ${why}`;
    return `${ticketLabel(ord)} ticket ${ord.ticketNumber} is in. ${missing ? `The ${ticketLabel(missing)} ticket wasn\u2019t made: ${why}` : `Failed: ${why}`}`;
  }
  if (s.state === "running") {
    if (!ord) return side === "reseller" ? "Opening the ticket." : "Opening the tickets.";
    return prov && !prov.ticketNumber ? `${ticketLabel(ord)} ticket ${ord.ticketNumber} is in. Opening the ${ticketLabel(prov)} ticket.` : `Adding the charges to ticket ${ord.ticketNumber}.`;
  }
  if (s.state !== "done" || !ord) return null;
  const provNumber = prov?.ticketNumber ?? null;
  if (!ops) return `Ticket ${ord.ticketNumber} with ${charges} and the signed PDF.${provNumber ? ` Phoneware is setting it up on ticket ${provNumber}.` : ""}`;
  const after = provNumber && prov ? ` ${ticketLabel(prov)} ticket ${provNumber}.` : side === "reseller" ? " No provisioning ticket for a Reseller\u2019s sale." : "";
  return `${ticketLabel(ord)} ticket ${ord.ticketNumber} with ${charges} and the signed PDF.${after}`;
}

/** Why the order holds at its tickets, for ops: the ticket that wasn't made and what Autotask said. null when it isn't held there. */
export function ticketHold(o: Order, side: OrderSide | null): { title: string; detail: string; retry: string } | null {
  if (!followsPartner(o) || o.autotask.state !== "failed") return null;
  const rows = ticketRows(o, side);
  const missing = rows.find((r) => !r.ticketNumber && r.state === "failed");
  const ord = rows.find((r) => r.kind === "order" && r.ticketNumber);
  const title = missing ? `The ${ticketLabel(missing)} ticket wasn\u2019t made.` : `Ticket ${ord?.ticketNumber ?? ""} needs a retry.`;
  const detail = `${o.autotask.error ?? "Autotask did not answer."} Omni tried ${plural(o.autotask.attempts, "time")}.`;
  const retry = ord
    ? `Retry: Omni makes only what\u2019s missing and never a second ${ticketLabel(ord)} ticket.`
    : "Retry: Omni looks for a ticket it already opened before it makes one, so it never makes a second.";
  return { title, detail, retry };
}

// ------------------------------------------------------------------ the tickets, for ops

function TicketItem({ row, order, orderNumber, busy, onRetry }: { row: TicketRow; order: Order; orderNumber: string | null; busy: boolean; onRetry: (() => void) | null }): React.ReactElement {
  const made = row.ticketId !== null && !!row.ticketNumber;
  const title =
    row.title ??
    (row.kind === "order"
      ? `Signed quote ${order.quoteNumber}`
      : row.kind === "provisioning"
        ? `Setting up ${order.customerName}`
        : "The support ticket the MAC came from");
  const metaParts = [
    row.kind === "order"
      ? made
        ? [plural(order.autotask.chargeIds.length, "charge"), order.autotask.state === "done" ? "signed PDF" : null].filter(Boolean).join(" \u00b7 ")
        : null
      : row.kind === "provisioning"
        ? orderNumber
          ? `Points to ${orderNumber}`
          : null
        : "MAC summary PDF and a note from Omni. No new ticket and no charges.",
  ].filter(Boolean);
  const meta = metaParts.join(" \u00b7 ");
  const failed = row.state === "failed";
  const queue = row.queueName;
  const cl = row.live?.checklist ?? null;
  // The name Omni recorded when it applied the checklist; a checklist Autotask applied itself (the Post Sale ticket's)
  // shows as Autotask has it, by its count, since Autotask doesn't say which library its items came from.
  const clName = row.checklistName ?? (cl ? "Checklist" : null);

  return (
    <li className={failed ? "tix-row fail" : "tix-row"} data-testid={`ticket-${row.kind}`} data-state={made ? row.state : `not-${row.state}`}>
      <span className="tix-ic" aria-hidden="true">
        <Icon name="ticket" size={15} />
      </span>
      <div className="tix-main">
        <span className="tix-k">
          <span className="tix-q">{row.kind === "source" ? `${queue ? `${queue} \u00b7 ` : ""}Source ticket` : ticketLabel(row)}</span>
          {made ? <span className="tix-n" data-testid={`ticket-number-${row.kind}`}>{row.ticketNumber}</span> : null}
          {row.live?.statusLabel ? (
            <span className="tix-status tag quiet" data-testid={`ticket-status-${row.kind}`}>
              {row.live.statusLabel}
            </span>
          ) : null}
        </span>
        <b className="tix-t">{title}</b>
        {meta ? <span className="tix-m">{meta}</span> : null}
        {made && row.kind !== "source" ? (
          clName ? (
            cl ? (
              <div className="cl" data-testid={`ticket-checklist-${row.kind}`}>
                <span className="cl-n">
                  <Icon name="list-checks" size={14} />
                  <span>{clName}</span>
                </span>
                <b>
                  {cl.done}/{cl.total}
                </b>
                <span
                  className="meter"
                  role="img"
                  aria-label={`${cl.done} of ${cl.total} done`}
                >
                  <i
                    style={{
                      width: `${Math.round((100 * cl.done) / cl.total)}%`,
                    }}
                  />
                </span>
              </div>
            ) : (
              <span className="tix-cl" data-testid={`ticket-checklist-${row.kind}`}>
                <Icon name="list-checks" size={13} />
                {clName}
              </span>
            )
          ) : row.checklistLibraryId !== null ? (
            <span className="tix-cl" data-testid={`ticket-checklist-${row.kind}`}>
              <Icon name="list-checks" size={13} />
              Checklist {row.checklistLibraryId}
            </span>
          ) : (
            <span className="tix-m" data-testid={`ticket-checklist-${row.kind}`}>No checklist.</span>
          )
        ) : null}
        {!made && !failed ? <span className="tix-m">{row.state === "running" ? "Opening it now." : "Not opened yet."}</span> : null}
        {failed ? (
          <div className="tix-fail" role="alert" data-testid="ticket-failed">
            <Icon name="triangle-alert" size={14} />
            <div>
              <b>{made ? "Failed" : "Not made"}: {row.error ?? "Autotask did not answer."}</b>
              <span className="sub-l">
                {plural(order.autotask.attempts, "try", "tries")}. A retry picks up where Omni stopped and makes only what{"\u2019"}s missing.
                {onRetry ? "" : " Billing ops or a Super admin retries it."}
              </span>
              {onRetry ? (
                <button className="btn small white" type="button" data-testid="ticket-retry" disabled={busy} onClick={onRetry}>
                  <Icon name="refresh-cw" size={13} />
                  {busy ? "Retrying" : "Retry"}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      {made && row.url ? (
        <a className="btn small white tix-go" href={row.url} target="_blank" rel="noopener noreferrer" data-testid={`ticket-link-${row.kind}`}>
          <Icon name="external-link" size={13} />
          Autotask
        </a>
      ) : null}
    </li>
  );
}

export function TicketsCard({ order, side, busy, onRetry }: { order: Order; side: OrderSide | null; busy: boolean; onRetry: (() => void) | null }): React.ReactElement | null {
  const rows = ticketRows(order, side);
  if (!rows.length) return null;
  const title = order.kind === "mac" ? "The source ticket" : rows.length > 1 ? "Tickets in Autotask" : "Ticket in Autotask";
  const note = side ? SIDE_NOTE[side] : null;
  const orderNumber = rows.find((r) => r.kind === "order")?.ticketNumber ?? null;
  return (
    <section className="card" data-testid="order-tickets">
      <div className="card-h">
        <h2>{title}</h2>
        {note ? <span className="note">{note}</span> : null}
      </div>
      <ul className="tix">
        {rows.map((r) => (
          <TicketItem key={r.kind} row={r} order={order} orderNumber={orderNumber} busy={busy} onRetry={onRetry} />
        ))}
        {side === "reseller" && followsPartner(order) ? (
          <li className="tix-row none" data-testid="ticket-none">
            <span className="tix-ic" aria-hidden="true">
              <Icon name="ban" size={14} />
            </span>
            <span className="tix-m">No provisioning ticket for a Reseller{"\u2019"}s sale.</span>
          </li>
        ) : null}
      </ul>
    </section>
  );
}

/** A dealer: Phoneware's setup ticket, without the Autotask link or the queue. Only for sales Phoneware sets up. */
export function SetupCard({ order, side }: { order: Order; side: OrderSide | null }): React.ReactElement | null {
  if (!followsPartner(order) || (side !== "house" && side !== "dealer")) return null;
  const prov = order.tickets?.find((t) => t.kind === "provisioning") ?? null;
  const cl = prov?.live?.checklist ?? null;
  return (
    <section className="card" data-testid="order-setup">
      <div className="card-h">
        <h2>Phoneware is setting it up</h2>
      </div>
      {prov && prov.state === "done" && cl ? (
        <div className="setup" data-testid="setup-meter">
          <span
            className="meter"
            role="img"
            aria-label={`${cl.done} of ${cl.total} steps done`}
          >
            <i
              style={{
                width: `${Math.round((100 * cl.done) / cl.total)}%`,
              }}
            />
          </span>
          <b>
            {cl.done} of {cl.total} steps done
          </b>
        </div>
      ) : null}
      <p className={prov && prov.state === "done" && cl ? "card-note" : "setup-t"}>
        {prov && prov.state === "done"
          ? `Phoneware works ${prov.checklistName || prov.checklistLibraryId !== null ? "through its setup checklist " : ""}on ticket ${prov.ticketNumber}.${cl ? " Omni shows how far along it is." : ""}`
          : "Phoneware\u2019s setup ticket is on its way."}
      </p>
    </section>
  );
}

const PLACEHOLDER = "One per line: the number, then what it\u2019s for.\n(951) 555-0148 main line, keep it";

/** A Telephone Number field's text: the digits the way people read them, or what was typed. */
function lineText(digits: string | null): string {
  return digits ? phoneWords(digits) : "";
}

/** The lines whose OneBill plan needs a Telephone Number, with their names, as billing requirements list them (#1763). */
function telephoneLines(req: BillingRequirements | null): Array<{ line: number; name: string }> {
  return (req?.lines ?? []).filter((l) => l.features.some((f) => f.featureName === TELEPHONE_NUMBER_FEATURE)).map((l) => ({ line: l.line, name: l.name }));
}

/** The line a 400 from the save names, when it names one. */
function refusedLine(err: unknown): number | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const body = err.body;
  return body && typeof body === "object" && "line" in body && typeof body.line === "number" ? body.line : null;
}

/** "a", "a and b", "a, b and c". */
function listed(parts: string[]): string {
  return parts.length <= 1 ? (parts[0] ?? "") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

export function DueNumbersCard({ order, side, onChanged }: { order: Order; side: OrderSide | null; onChanged: (next: Order) => void }): React.ReactElement | null {
  const { me, api, everyDealer, canWrite } = useSession();
  const ops = everyDealer;
  // null: untouched, so the field shows what the order has (and follows it as Omni re-reads the order).
  const [date, setDate] = useState<string | null>(null);
  const [numbers, setNumbers] = useState<string | null>(null);
  // Each line's Telephone Number as typed, by line; a line not here shows what the order has (#1763).
  const [lineEdits, setLineEdits] = useState<Record<number, string>>({});
  const [leftLines, setLeftLines] = useState<Record<number, true>>({});
  const [refused, setRefused] = useState<{ line: number; error: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<{ dueDate: string | null; numbers: number; lines: number; by: string } | null>(null);
  const [stale, setStale] = useState<{ dueDate: string | null; by: string | null; at: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const editable = order.kind !== "mac" && followsPartner(order) && order.status !== "complete" && canWrite("orders.edit", { id: order.dealerId });
  // Which lines need a Telephone Number, read from OneBill's plans the way billing approval reads them.
  const requirements = useLoad<BillingRequirements | null>(() => (editable ? api.billingRequirements(order.id) : Promise.resolve(null)), [order.id, editable]);

  if (order.kind === "mac") return null;
  const hadDate = order.dueDate ?? "";
  const hadNumbers = order.numbersNote ?? "";


  if (!editable) {
    const empty = !hadDate && !hadNumbers.trim();
    return (
      <section className="card" data-testid="order-due">
        <div className="card-h">
          <h2>Due date and numbers</h2>
        </div>
        <dl className="facts">
          <div>
            <dt>Due date</dt>
            <dd data-testid="due-date-read">{hadDate ? dueText(hadDate) : <span className="muted">None set</span>}</dd>
          </div>
          <div>
            <dt>Numbers and what they{"\u2019"}re for</dt>
            <dd className="dn-read" data-testid="due-numbers-read">
              {hadNumbers.trim() ? hadNumbers : <span className="muted">None given</span>}
            </dd>
          </div>
        </dl>
        {empty && !followsPartner(order) ? <p className="card-note">This order came before due dates and numbers were on orders.</p> : null}
      </section>
    );
  }

  const shownDate = date ?? hadDate;
  const shownNumbers = numbers ?? hadNumbers;
  const dateChanged = shownDate !== hadDate;
  const numbersChanged = shownNumbers.trim() !== hadNumbers.trim();
  // Omni writes a due date onto the tickets and adds numbers as a note; neither can be taken back off from here.
  const clearsDate = dateChanged && !shownDate;
  const clearsNumbers = numbersChanged && !shownNumbers.trim();
  const badDate = !!shownDate && !/^\d{4}-\d{2}-\d{2}$/.test(shownDate);

  // Each line's Telephone Number, until Phoneware approves billing with what it confirmed.
  const lines = telephoneLines(requirements.data);
  const waiting = order.billingApproval?.state === "waiting";
  const mainPhone = requirements.data?.mainPhone ?? null;
  const lineState = lines.map((l) => {
    const had = sellerLineNumber(order.lineNumbers, l.line);
    const text = lineEdits[l.line] ?? lineText(had);
    const digits = text.trim() ? telephoneDigits(text) : null;
    const bad = !!text.trim() && digits === null;
    const changed = text.trim() ? !bad && digits !== had : had !== null;
    return { ...l, had, text, digits, bad, changed };
  });
  const linesChanged = waiting ? lineState.filter((l) => l.changed) : [];
  const badLine = waiting && lineState.some((l) => l.bad);

  const ready = (dateChanged || numbersChanged || linesChanged.length > 0) && !clearsDate && !clearsNumbers && !badDate && !badLine && !busy;
  // Whether a save lands on a ticket in Autotask now, or waits on the order for Omni to open one.
  const ticket = (order.tickets?.length ?? 0) > 0 || order.autotask.ticketId !== null;
  const orderQueue = order.tickets?.find((t) => t.kind === "order")?.queueName ?? null;
  const where = !ops
    ? `The due date and the numbers go on Phoneware\u2019s ticket for ${order.customerName}.`
    : side === "reseller"
      ? `The due date and the numbers go on the ${orderQueue ?? "order"} ticket.`
      : "The due date goes on both tickets. The numbers go on the Provisioning ticket as a note.";

  async function save(): Promise<void> {
    const body: OrderPatch = { expectedUpdatedAt: order.updatedAt };
    if (dateChanged) body.dueDate = shownDate;
    if (numbersChanged) body.numbersNote = shownNumbers.trim();
    if (linesChanged.length > 0) body.lineNumbers = linesChanged.map((l) => ({ line: l.line, number: l.digits ?? "" }));
    setBusy(true);
    setSaved(null);
    setStale(null);
    setError(null);
    setRefused(null);
    try {
      const next = await api.patchOrder(order.id, body);
      setDate(null);
      setNumbers(null);
      setLineEdits({});
      setLeftLines({});
      onChanged(next);
      // Say what this save wrote, not what someone else saved before it.
      setSaved({
        dueDate: body.dueDate ? (next.dueDate ?? null) : null,
        numbers: body.numbersNote !== undefined ? numberCount(next.numbersNote ?? "") : 0,
        lines: body.lineNumbers?.length ?? 0,
        by: me.user.name,
      });
    } catch (err) {
      const line = refusedLine(err);
      if (line !== null) {
        // The server names the line it refused; the message sits under that field.
        setRefused({ line, error: errorText(err) });
      } else if (err instanceof ApiError && err.status === 409) {
        // Someone saved first: read the order again, show its due date, and keep the numbers this person typed.
        try {
          const fresh = await api.order(order.id);
          setDate(null);
          setNumbers(shownNumbers);
          onChanged(fresh);
          setStale({ dueDate: fresh.dueDate ?? null, by: fresh.dueDateSetBy ?? null, at: fresh.dueDateSetAt ?? null });
        } catch (again) {
          setError(errorText(again));
        }
      } else {
        setError(errorText(err));
      }
    } finally {
      setBusy(false);
    }
  }

  const savedParts = saved
    ? listed(
        [
          saved.dueDate ? `Due ${dueText(saved.dueDate)}` : null,
          saved.numbers ? plural(saved.numbers, "number") : null,
          saved.lines ? `the Telephone Number on ${plural(saved.lines, "line")}` : null,
        ].filter((p): p is string => p !== null),
      )
    : "";
  // A save of only line numbers writes nothing to Autotask: they wait on the order for billing approval.
  const onlyLines = !!saved && !saved.dueDate && !saved.numbers && saved.lines > 0;

  return (
    <section className="card" data-testid="order-due">
      <div className="card-h">
        <h2>Due date and numbers</h2>
        <span className="note">{!ops ? "Goes on Phoneware\u2019s ticket" : side === "reseller" ? "Written to the ticket" : "Written to the tickets"}</span>
      </div>
      {saved ? (
        <div className="callout ok dn-msg" role="status" data-testid="due-saved">
          <Icon name="circle-check" size={15} />
          <div>
            <b>{onlyLines || !ticket ? "Saved on the order." : ops ? "Written to the tickets." : "Added to Phoneware\u2019s ticket."}</b>
            <span className="sub-l">
              {savedParts ? `${savedParts.charAt(0).toUpperCase()}${savedParts.slice(1)}, saved` : "Saved"} just now by {saved.by}.
              {onlyLines ? " Phoneware confirms them at billing approval." : ticket ? "" : " Omni puts them on the tickets when it opens them."}
            </span>
          </div>
        </div>
      ) : null}
      {stale ? (
        <div className="callout stop dn-msg" role="alert" data-testid="due-stale">
          <Icon name="triangle-alert" size={15} />
          <div>
            <b>Someone changed this order since you opened it, so Omni read it again.</b>
            <span className="sub-l">
              {stale.by ? `${stale.by} set the due date to ` : "The due date is now "}
              {stale.dueDate ? dueText(stale.dueDate) : "none set"}
              {stale.by && stale.at ? ` ${atTime(stale.at)}` : ""}. Your numbers are still in the box. Check both, then save again.
            </span>
          </div>
        </div>
      ) : null}
      {error ? (
        <div className="callout stop dn-msg" role="alert" data-testid="due-error">
          <Icon name="triangle-alert" size={15} />
          <div>
            <b>Not saved.</b>
            <span className="sub-l">{error}</span>
          </div>
        </div>
      ) : null}
      <div className="dn">
        <label className="field">
          <span>Due date</span>
          <input
            className="input"
            type="date"
            data-testid="due-date"
            value={shownDate}
            onChange={(e) => {
              setDate(e.target.value);
              setSaved(null);
            }}
          />
          <span className="field-note">When {order.customerName} needs it working.</span>
        </label>
        <label className="field">
          <span>Numbers and what they{"\u2019"}re for</span>
          <textarea
            className="input"
            rows={4}
            data-testid="due-numbers"
            placeholder={PLACEHOLDER}
            value={shownNumbers}
            onChange={(e) => {
              setNumbers(e.target.value);
              setSaved(null);
            }}
          />
        </label>
      </div>
      {requirements.error ? (
        <div className="dn-lines" data-testid="due-lines-error">
          <p className="field-note dn-warn">
            Omni couldn{"\u2019"}t read which lines need a Telephone Number. {requirements.error.message}
          </p>
          <button className="btn small white" type="button" data-testid="due-lines-reread" onClick={() => requirements.reload()}>
            <Icon name="refresh-cw" size={13} />
            Read again
          </button>
        </div>
      ) : lines.length > 0 ? (
        <div className="dn-lines" data-testid="due-lines">
          <span className="label">Telephone Number on each line</span>
          <ul className="dn-line-list">
            {lineState.map((l) => {
              const id = `due-line-${l.line}`;
              const confirmed = order.billingApproval?.lineValues?.find((v) => v.line === l.line && v.featureName === TELEPHONE_NUMBER_FEATURE)?.value ?? null;
              const err = refused?.line === l.line ? refused.error : l.bad && leftLines[l.line] ? "A Telephone Number is 10 digits." : null;
              return (
                <li className="dn-line" key={l.line} data-testid={id}>
                  <label className="dn-line-n" htmlFor={`${id}-number`}>
                    <b>{l.name}</b>
                    <span className="muted">Line {l.line + 1}</span>
                  </label>
                  {waiting ? (
                    <div className="dn-line-f">
                      <input
                        id={`${id}-number`}
                        className={err ? "input bad" : "input"}
                        data-testid={`${id}-number`}
                        inputMode="tel"
                        autoComplete="off"
                        placeholder="10 digits"
                        aria-invalid={err ? true : undefined}
                        value={l.text}
                        onChange={(e) => {
                          const text = e.target.value;
                          setLineEdits((v) => ({ ...v, [l.line]: text }));
                          setSaved(null);
                          if (refused?.line === l.line) setRefused(null);
                        }}
                        onBlur={() => setLeftLines((v) => ({ ...v, [l.line]: true }))}
                      />
                      {err ? (
                        <span className="err" role="alert" data-testid={`${id}-error`}>
                          {err}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <span className="dn-line-v" data-testid={`${id}-confirmed`}>
                      {confirmed ? phoneWords(confirmed) : <span className="muted">None</span>}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="field-note" data-testid="due-lines-note">
            {waiting ? "Phoneware confirms each number at billing approval." : "Phoneware confirmed these at billing approval."}
            {waiting && mainPhone ? (
              <>
                {" A line left empty starts from the main number in Autotask, "}
                <span className="dn-num">{phoneWords(mainPhone)}</span>.
              </>
            ) : null}
          </p>
        </div>
      ) : null}
      {clearsDate || clearsNumbers || badDate ? (
        <p className="field-note dn-warn" data-testid="due-hint">
          {badDate
            ? "Pick a date from the calendar."
            : clearsDate
              ? "Pick a date. Omni can\u2019t take a due date off the tickets."
              : "Omni adds the numbers to the ticket as a note and can\u2019t take one off. Change them instead."}
        </p>
      ) : null}
      <div className="dn-foot">
        <p className="card-note" data-testid="due-where">
          {where}
          {ticket ? "" : " Omni puts them on the tickets when it opens them."}
        </p>
        <button className="btn primary" type="button" data-testid="due-save" disabled={!ready} onClick={() => void save()}>
          <Icon name="check" size={15} />
          {busy ? "Saving" : "Save"}
        </button>
      </div>
    </section>
  );
}
